'use client'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { useSaveFromConversationMutation } from '~/data/api/client'
import { parseError } from '~/data/api/utils'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

type SaveSkillFromConversationDialogProps = {
  open: boolean
  conversationId: string | null
  onClose: () => void
  onSaved?: () => void
}

export default function SaveSkillFromConversationDialog({
  open,
  conversationId,
  onClose,
  onSaved
}: SaveSkillFromConversationDialogProps) {
  const t = useTranslations('settings.assistantSkills')
  const orgId = useCurrentOrgId()
  const [saveFromConversation, { isLoading }] = useSaveFromConversationMutation()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [shareScope, setShareScope] = useState<'personal' | 'organization'>('personal')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setDescription('')
    setShareScope('personal')
    setError(null)
  }, [open, conversationId])

  const handleSubmit = async () => {
    if (!orgId || !conversationId) {
      setError(t('saveFromConversationUnavailable'))
      return
    }
    if (!name.trim()) {
      setError(t('saveFromConversationNameRequired'))
      return
    }

    setError(null)
    try {
      await saveFromConversation({
        'X-Organization-Id': orgId,
        assistantSkillSaveFromConversationParams: {
          conversationId,
          name: name.trim(),
          description: description.trim() || undefined,
          shareScope
        }
      }).unwrap()
      onSaved?.()
      onClose()
    } catch (error: unknown) {
      setError(parseError(error, { default: t('formSaveError') }))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('saveFromConversationTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('formName')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t('formDescription')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            select
            label={t('formShareScope')}
            value={shareScope}
            onChange={(event) => setShareScope(event.target.value as 'personal' | 'organization')}
            fullWidth
          >
            <MenuItem value="personal">{t('tabPersonal')}</MenuItem>
            <MenuItem value="organization">{t('tabOrganization')}</MenuItem>
          </TextField>
          {error ? (
            <span style={{ color: 'var(--mui-palette-error-main)', fontSize: '0.875rem' }}>{error}</span>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('formCancel')}
        </Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={isLoading || !conversationId}>
          {t('saveFromConversationSubmit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
